import {ExportBottomSheetData, ExportBottomSheetFormat, ExportBottomSheetReport} from 'app/shared/components/export-bottom-sheet/export-bottom-sheet.component';
import {VAT_EXPORT_COPY, VAT_OBJEKT_DIALOG_COPY} from '@vat/vat-copy';
import {VatExportAction, VatExportReportID, VatReturn} from './vat-export.types';

/**
 * Konfiguration des ExportBottomSheetComponent (Spec 7): Vordruck-PDF, Herleitung-Excel, Kz-48-Nachweis und
 * Objekt-Abrechnung aktiv; ELSTER-XML und DATEV in Phase 1 deaktiviert mit disabledReason. Der Bericht bestimmt die
 * Aktion (A5) – einzig die Objekt-Abrechnung (Task C2) liest zusätzlich das Format (PDF/Excel).
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
export const VAT_EXPORT_FORMATS: readonly ExportBottomSheetFormat[] = ['pdf', 'excel'];

export interface VatExportSelection {
    format: ExportBottomSheetFormat;
    saveToFolder: boolean;
    reportID?: string;
}

export const hasReleasedSnapshot = (vatReturn: Pick<VatReturn, 'status' | 'kennzahlen'>): boolean =>
    vatReturn.status !== 'ENTWURF' && vatReturn.kennzahlen !== null && vatReturn.kennzahlen !== undefined;

export const buildVatExportReports = (vatReturn: Pick<VatReturn, 'status' | 'kennzahlen'>): ExportBottomSheetReport[] => {
    const released = hasReleasedSnapshot(vatReturn);
    const copy = VAT_EXPORT_COPY.reports;
    const snapshotGate = released ? {} : {disabled: true, disabledReason: VAT_EXPORT_COPY.notReleased};
    return [
        {id: 'vordruck-pdf' satisfies VatExportReportID, label: copy.vordruckPdf.label, description: copy.vordruckPdf.description, icon: 'heroicons_outline:document-text', ...snapshotGate},
        {id: 'herleitung-excel' satisfies VatExportReportID, label: copy.herleitungExcel.label, description: copy.herleitungExcel.description, icon: 'heroicons_outline:table', ...snapshotGate},
        {id: 'kz48-pdf' satisfies VatExportReportID, label: copy.kz48Pdf.label, description: copy.kz48Pdf.description, icon: 'heroicons_outline:document-report'},
        {id: 'objekt-abrechnung' satisfies VatExportReportID, label: VAT_OBJEKT_DIALOG_COPY.report.label, description: VAT_OBJEKT_DIALOG_COPY.report.description, icon: 'heroicons_outline:office-building'},
        {id: 'elster-xml' satisfies VatExportReportID, label: copy.elsterXml.label, description: copy.elsterXml.description, icon: 'heroicons_outline:cloud-upload', disabled: true, disabledReason: copy.elsterXml.disabledReason},
        {id: 'datev' satisfies VatExportReportID, label: copy.datev.label, description: copy.datev.description, icon: 'heroicons_outline:lock-closed', disabled: true, disabledReason: copy.datev.disabledReason},
    ];
};

export const buildVatExportSheetData = (vatReturn: Pick<VatReturn, 'status' | 'kennzahlen'>): ExportBottomSheetData => ({
    title: VAT_EXPORT_COPY.sheetTitle,
    description: VAT_EXPORT_COPY.sheetDescription,
    reportsTitle: VAT_EXPORT_COPY.reportsTitle,
    reports: buildVatExportReports(vatReturn),
    selectedReportID: 'vordruck-pdf',
    formats: VAT_EXPORT_FORMATS,
    pdfLabel: VAT_EXPORT_COPY.pdfLabel,
    pdfDescription: VAT_EXPORT_COPY.pdfDescription,
    excelDescription: VAT_EXPORT_COPY.excelDescription,
});

export const buildKz48SheetData = (): ExportBottomSheetData => ({
    title: VAT_EXPORT_COPY.reports.kz48Pdf.label,
    description: VAT_EXPORT_COPY.reports.kz48Pdf.description,
    reportsTitle: VAT_EXPORT_COPY.reportsTitle,
    reports: [buildVatExportReports({status: 'GEPRUEFT', kennzahlen: null}).find(r => r.id === 'kz48-pdf')],
    selectedReportID: 'kz48-pdf',
    formats: ['pdf'],
    pdfLabel: VAT_EXPORT_COPY.pdfLabel,
    pdfDescription: VAT_EXPORT_COPY.reports.kz48Pdf.description,
});

const ACTION_BY_REPORT: Record<string, VatExportAction> = {
    'vordruck-pdf': 'VORDRUCK_PDF',
    'herleitung-excel': 'HERLEITUNG_EXCEL',
    'kz48-pdf': 'KZ48_PDF',
};

export const OBJEKT_REPORT_ID: VatExportReportID = 'objekt-abrechnung';

export const resolveVatExportAction = (selection: VatExportSelection | null | undefined): VatExportAction | null => {
    if (!selection?.reportID) {
        return null;
    }
    if (selection.reportID === OBJEKT_REPORT_ID) {
        return selection.format === 'excel' ? 'OBJEKT_EXCEL' : 'OBJEKT_PDF';
    }
    return ACTION_BY_REPORT[selection.reportID] || null;
};
